import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';

// Modal overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Modal container
const ModalContainer = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1F2937' : '#ffffff'};
  border-radius: 12px;
  width: 90%;
  max-width: 640px;
  box-shadow: ${({ theme }) =>
    theme?.name === 'dark'
      ? '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
  animation: slideIn 0.3s ease-out;
  position: relative;
  overflow: hidden;

  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// Modal header
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#F3F4F6' : '#0F172A')};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#374151' : '#f3f4f6'};
  border: none;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#9CA3AF' : '#6b7280')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark' ? '#4B5563' : '#e5e7eb'};
    color: ${({ theme }) => (theme?.name === 'dark' ? '#F3F4F6' : '#374151')};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Modal body
const ModalBody = styled.div`
  padding: 24px;
`;

// Form elements
const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#e2e8f0' : '#374151')};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme, error }) =>
      error
        ? '#ef4444'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => (theme?.name === 'dark' ? '#64748b' : '#94a3b8')};
  }
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme, error }) =>
      error
        ? '#ef4444'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  transition: all 0.2s;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  option {
    background: ${({ theme }) =>
      theme?.name === 'dark' ? '#1F2937' : '#ffffff'};
    color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme, error }) =>
      error
        ? '#ef4444'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  transition: all 0.2s;
  resize: vertical;
  min-height: 120px;
  line-height: 1.6;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => (theme?.name === 'dark' ? '#64748b' : '#94a3b8')};
  }
`;

const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
`;

// Modal footer
const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#374151' : '#e2e8f0')};
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#111827' : '#f9fafb'};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#9CA3AF' : '#64748b')};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#e2e8f0'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #fa4616, #ff6b3b);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AttachmentWrapper = styled.div`
  margin-top: 20px;
`;

const AttachmentLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#e2e8f0' : '#374151')};
  margin-bottom: 8px;
`;

// Icons
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Note type options
const NOTE_TYPES = [
  { value: 'event', label: 'Event' },
  { value: 'issues', label: 'Issues' },
  { value: 'business', label: 'Business' },
  { value: 'staff', label: 'Staff' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'instructions', label: 'Instructions' },
  { value: 'payouts', label: 'Payouts' },
  { value: 'other', label: 'Other' },
];

const AddNoteModal = ({
  isOpen,
  onClose,
  onSubmit,
  note: editingNote,
  defaultType = 'other',
  modalTitle,
}) => {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [type, setType] = useState(defaultType);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  const isEditing = !!editingNote;

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Populate form when editing, reset when closing
  useEffect(() => {
    if (isOpen && editingNote) {
      setTitle(editingNote.title || '');
      setNote(editingNote.note || '');
      setType(editingNote.type || defaultType);
      // If editing and there's an existing image, create attachment object
      if (editingNote.image && editingNote.image_id) {
        setAttachments([
          {
            id: editingNote.image_id,
            name: 'Attached Image',
            size: 0,
            sizeFormatted: '',
            url: editingNote.image,
            type: 'image/*',
            isImage: true,
          },
        ]);
      } else {
        setAttachments([]);
      }
    } else if (!isOpen) {
      setTitle('');
      setNote('');
      setType(defaultType);
      setAttachments([]);
      setErrors({});
    }
  }, [isOpen, editingNote, defaultType]);

  const validate = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!note.trim()) {
      newErrors.note = 'Note content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    const noteData = {
      title: title.trim(),
      note: note.trim(),
      type,
      ...(isEditing && { id: editingNote.id }),
      // Include image_id and image URL if there's an attachment
      ...(attachments.length > 0 && {
        image_id: attachments[0].id,
        image: attachments[0].url,
      }),
    };

    const success = await onSubmit(noteData, isEditing);

    if (success) {
      onClose();
    }

    setIsSubmitting(false);
  };

  const handleClose = () => {
    setTitle('');
    setNote('');
    setType(defaultType);
    setAttachments([]);
    setErrors({});
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getModalTitle = () => {
    if (modalTitle) return isEditing ? 'Edit Note' : modalTitle;
    return isEditing ? 'Edit Note' : 'Add Note';
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{getModalTitle()}</ModalTitle>
          <CloseButton type="button" onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label htmlFor="note_title">Title</Label>
              <Input
                ref={inputRef}
                id="note_title"
                type="text"
                placeholder="Enter note title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: '' }));
                  }
                }}
                error={!!errors.title}
              />
              {errors.title && <ErrorText>{errors.title}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="note_type">Type</Label>
              <Select
                id="note_type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {NOTE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="note_content">Note</Label>
              <Textarea
                id="note_content"
                placeholder="Enter note content..."
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  if (errors.note) {
                    setErrors((prev) => ({ ...prev, note: '' }));
                  }
                }}
                error={!!errors.note}
              />
              {errors.note && <ErrorText>{errors.note}</ErrorText>}
            </FormGroup>

            <AttachmentWrapper>
              <AttachmentLabel>Attachment (optional)</AttachmentLabel>
              <AttachmentUpload
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                maxFiles={1}
                channel="SMS"
                defaultExpanded={attachments.length > 0}
              />
            </AttachmentWrapper>
          </ModalBody>

          <ModalFooter>
            <CancelButton type="button" onClick={handleClose}>
              Cancel
            </CancelButton>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? isEditing
                  ? 'Saving...'
                  : 'Adding...'
                : isEditing
                ? 'Save Changes'
                : 'Add Note'}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};

AddNoteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    note: PropTypes.string,
    type: PropTypes.string,
    image: PropTypes.string,
    image_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  defaultType: PropTypes.string,
  modalTitle: PropTypes.string,
};

export default AddNoteModal;
